import React, { useEffect, useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { useGetLereToursPricingSuggestions } from '~/hooks/apis/lere/useGetLereToursPricingSuggestions';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import ToursCapacitySalesFormatter from './formatters/ToursCapacitySalesFormatter';
import ToursIdsFormatter from './formatters/ToursIdsFormatter';
import ToursPricingFormatter from './formatters/ToursPricingFormatter';
import { TOURS_PRICING_SAMPLE_DATA } from './toursPricingSampleData';
import {
  LerePaginatedResponse,
  LereTabComponentProps,
  LereToursPricingSuggestion,
  LereToursPricingSuggestionsParams,
  SuggestedActionType,
} from './types';

const EMPTY_RESPONSE: LerePaginatedResponse<LereToursPricingSuggestion> = {
  result: [],
  total: 0,
};

const SUGGESTED_ACTION_TYPE_OPTIONS: Array<{ value: SuggestedActionType; label: string }> = [
  { value: 'surcharge', label: 'Surcharge' },
  { value: 'discount', label: 'Discount' },
];

type AppliedFilters = {
  offer_bk?: string;
  offer_salesforce_bk?: string;
  suggested_action_type?: SuggestedActionType;
};

function formatDate(value: string | null): string {
  if (!value) {
    return '—';
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : value;
}

function formatNumber(value: number | null): string {
  if (value == null) {
    return '—';
  }
  return value.toLocaleString();
}

export default function RMToursPricingSuggestions({ onLereError }: LereTabComponentProps) {
  const { paginationModel, setPaginationModel, setPage } = useDataGridPagination({ page: 0, pageSize: 20 });

  const [offerBkInput, setOfferBkInput] = useState('');
  const [salesforceIdInput, setSalesforceIdInput] = useState('');
  const [actionTypeInput, setActionTypeInput] = useState<SuggestedActionType | ''>('');
  const [useLiveData, setUseLiveData] = useState(false);

  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({});

  const queryParams = useMemo<LereToursPricingSuggestionsParams>(
    () => ({
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      ...appliedFilters,
    }),
    [paginationModel.page, paginationModel.pageSize, appliedFilters],
  );

  const toursPricingSuggestionsQuery = useGetLereToursPricingSuggestions(queryParams, { enabled: useLiveData });

  const samplePaginatedData = useMemo<LerePaginatedResponse<LereToursPricingSuggestion>>(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    const end = start + paginationModel.pageSize;
    return {
      result: TOURS_PRICING_SAMPLE_DATA.slice(start, end),
      total: TOURS_PRICING_SAMPLE_DATA.length,
    };
  }, [paginationModel.page, paginationModel.pageSize]);

  const toursPricingSuggestionsData = useLiveData
    ? toursPricingSuggestionsQuery.data ?? EMPTY_RESPONSE
    : samplePaginatedData;

  useEffect(() => {
    if (toursPricingSuggestionsQuery.error) {
      onLereError(toursPricingSuggestionsQuery.error);
    }
  }, [toursPricingSuggestionsQuery.error, onLereError]);

  const handleSearch = () => {
    setAppliedFilters({
      offer_bk: offerBkInput || undefined,
      offer_salesforce_bk: salesforceIdInput || undefined,
      suggested_action_type: actionTypeInput || undefined,
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setOfferBkInput('');
    setSalesforceIdInput('');
    setActionTypeInput('');
    setAppliedFilters({});
    setPage(0);
  };

  const handleActionTypeChange = (event: SelectChangeEvent) => {
    setActionTypeInput(event.target.value as SuggestedActionType | '');
  };

  const columns = useMemo<Array<GridColDef<LereToursPricingSuggestion>>>(() => {
    const compactCol: Partial<GridColDef<LereToursPricingSuggestion>> = {
      minWidth: 120,
      flex: 1,
    };

    return [
      {
        ...compactCol,
        field: 'offer_name',
        headerName: 'Offer Name',
        minWidth: 200,
        flex: 2,
        renderCell: ({ row }) => {
          const name = row.offer_name || '—';
          if (row.offer_url) {
            return (
              <Link href={row.offer_url} target="_blank" rel="noopener noreferrer" underline="hover">
                {name}
              </Link>
            );
          }
          return name;
        },
      },
      {
        field: 'ids',
        headerName: 'IDs',
        display: 'flex',
        flex: 2,
        minWidth: 220,
        sortable: false,
        filterable: false,
        renderCell: ToursIdsFormatter,
      },
      {
        ...compactCol,
        field: 'suggested_action_type',
        headerName: 'Action Type',
        renderCell: ({ row }) => row.suggested_action_type.charAt(0).toUpperCase() + row.suggested_action_type.slice(1),
      },
      {
        ...compactCol,
        field: 'departure_date',
        headerName: 'Departure Date',
        minWidth: 135,
        renderCell: ({ row }) => formatDate(row.departure_date),
      },
      {
        field: 'pricing',
        headerName: 'Pricing',
        display: 'flex',
        flex: 2,
        minWidth: 200,
        sortable: false,
        filterable: false,
        renderCell: ToursPricingFormatter,
      },
      {
        field: 'capacity_sales',
        headerName: 'Capacity & Sales',
        display: 'flex',
        flex: 2,
        minWidth: 250,
        sortable: false,
        filterable: false,
        renderCell: ToursCapacitySalesFormatter,
      },
      {
        ...compactCol,
        field: 'offer_country_name',
        headerName: 'Country',
        renderCell: ({ row }) => row.offer_country_name || '—',
      },
      {
        ...compactCol,
        field: 'handback_date',
        headerName: 'Handback Date',
        minWidth: 135,
        renderCell: ({ row }) => formatDate(row.handback_date),
      },
      {
        ...compactCol,
        field: 'leadtime',
        headerName: 'Leadtime',
        minWidth: 100,
        renderCell: ({ row }) => formatNumber(row.leadtime),
      },
    ];
  }, []);

  return (
    <Box>
      <Typography variant="h2" fontSize="2em" fontWeight="medium">
        RM Tours Pricing Suggestions
      </Typography>
      <Typography variant="subtitle1" fontWeight="medium" sx={{ mt: 3 }}>
        Filters
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1, mb: 3 }}>
        <TextField
          label="Offer ID"
          size="small"
          disabled={!useLiveData}
          value={offerBkInput}
          onChange={(e) => setOfferBkInput(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <TextField
          label="Salesforce ID"
          size="small"
          disabled={!useLiveData}
          value={salesforceIdInput}
          onChange={(e) => setSalesforceIdInput(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }} disabled={!useLiveData}>
          <InputLabel>Action Type</InputLabel>
          <Select value={actionTypeInput} label="Action Type" onChange={handleActionTypeChange}>
            <MenuItem value="">All</MenuItem>
            {SUGGESTED_ACTION_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="text"
          disabled={!useLiveData || toursPricingSuggestionsQuery.isFetching}
          onClick={handleClearFilters}
        >
          Clear filters
        </Button>
        <Button
          variant="contained"
          disabled={!useLiveData || toursPricingSuggestionsQuery.isFetching}
          onClick={handleSearch}
        >
          Search
        </Button>
      </Stack>

      <DataGrid
        autoHeight
        columns={columns}
        rows={toursPricingSuggestionsData.result}
        rowCount={toursPricingSuggestionsData.total}
        loading={useLiveData && toursPricingSuggestionsQuery.isFetching}
        pagination
        paginationMode="server"
        pageSizeOptions={[10, 20, 50]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowHeight={() => 'auto'}
        getRowId={(row) => row.suggestion_id}
        slots={{
          pagination: GridPagination,
        }}
        disableRowSelectionOnClick
        disableColumnMenu
        sx={{ width: '100%' }}
      />

      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Switch
              checked={useLiveData}
              onChange={(e) => {
                setUseLiveData(e.target.checked);
                setPage(0);
              }}
            />
          }
          label={
            <Typography variant="body2" color={useLiveData ? 'text.primary' : 'text.secondary'}>
              Use Live Data {!useLiveData && '(currently showing sample data)'}
            </Typography>
          }
        />
      </Box>
    </Box>
  );
}
